import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Lightbulb, AlertTriangle, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { format, getDay } from 'date-fns';
import { auth } from '../../config/firebase'; // Adjust path if needed
import { SocialIcon } from '../icons/SocialIcon'; // Added import for SocialIcon
import type { SocialPlatform } from '../icons/SocialIcon'; // Adjust path if needed

// --- Types based on API response ---
interface HourlyEngagement {
    hour: number;
    score: number;
    reason?: string;
}

// Map from day name (e.g., "lundi", "tuesday") to hourly engagement data
type DailyHourlyEngagement = Record<string, HourlyEngagement[]>;

interface TopRecommendation {
    day: string; // e.g., "Lundi"
    startTime: string; // "HH:mm"
    endTime: string;   // "HH:mm"
    score: number;
    reason?: string;
}

interface PlatformAnalysis {
    dailyHourlyEngagement: DailyHourlyEngagement; // Added
    topRecommendations: TopRecommendation[]; // Updated structure (added 'day')
    bestDays: string[]; // e.g., ["Lundi", "Mercredi", "Vendredi"]
    worstDays: string[]; // e.g., ["Samedi", "Dimanche"]
    optimizedFrequency: string;
    platformSpecificNotes?: string;
    metadata: {
        brandId: string;
        personaId: string | null;
        platform: SocialPlatform;
        timestamp: string;
    };
    nextBestTime: string;
}

// --- Component Props ---
interface AIBestTimeCardProps {
    brandId: string | null;
    brandName?: string | null;
    selectedPlatforms: SocialPlatform[];
}

// --- Chart Config ---
const chartConfig = {
    score: {
        label: "Engagement Score",
        color: "#6d28d9", // Changed to a static primary-700 equivalent purple
    },
};

// --- Helper Functions ---
const formatHourTick = (hour: number) => `${hour}h`;

const dayNameToIndex = (dayName: string): number => {
  // date-fns: Sunday=0, Monday=1, ..., Saturday=6
  const lowerDay = dayName.toLowerCase();
  switch (lowerDay) {
    case 'sunday': return 0;
    case 'monday': return 1;
    case 'tuesday': return 2;
    case 'wednesday': return 3;
    case 'thursday': return 4;
    case 'friday': return 5;
    case 'saturday': return 6;
    default: return -1; // Invalid day
  }
};

// Helper function to capitalize the first letter of a string
const capitalize = (s: string) => {
  if (typeof s !== 'string' || s.length === 0) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Helper function to get the API day key (e.g., 'lundi') from a date-fns day index
// date-fns: Sunday=0, Monday=1, ..., Saturday=6
// API: expects 'lundi', 'mardi', etc.
const getApiDayKeyFromDateFns = (dayIndex: number): string => {
    const daysMap: { [key: number]: string } = {
        0: 'dimanche', // Sunday
        1: 'lundi',    // Monday
        2: 'mardi',    // Tuesday
        3: 'mercredi', // Wednesday
        4: 'jeudi',    // Thursday
        5: 'vendredi', // Friday
        6: 'saturday'  // Saturday - Note: API example uses lowercase french names
    };
    // Fallback to 'lundi' or handle as error if needed, for now using 'lundi'
    return daysMap[dayIndex] || 'lundi'; 
};

// --- Component ---
export const AIBestTimeCard: React.FC<AIBestTimeCardProps> = ({ brandId, brandName, selectedPlatforms }) => {
    const { t, i18n } = useTranslation();
    const [apiData, setApiData] = useState<PlatformAnalysis[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedTab, setSelectedTab] = useState<SocialPlatform | null>(null);
    const [selectedDay, setSelectedDay] = useState<string | null>(null);

    const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Added for debounce

    useEffect(() => {
        // Define the actual data fetching logic inside useEffect
        // so it has access to the latest state and props.
        const internalFetchData = async () => {
            if (!brandId || selectedPlatforms.length === 0) {
                // This case should ideally be handled by the conditions below,
                // but as a safeguard within the debounced function:
                setApiData(null);
                setError(null);
                setIsLoading(false);
                setSelectedTab(null);
                setSelectedDay(null);
                return;
            }

            setIsLoading(true);
            setError(null);
            // setApiData(null); // Cleared before setting timeout

            try {
                const user = auth.currentUser;
                if (!user) {
                    throw new Error("User not authenticated.");
                }
                const token = await user.getIdToken();

                const response = await fetch('https://api.socialness.ai/ai-coach/posting-time/analyze', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        brandId: brandId,
                        personaId: "", // Assuming empty for now
                        platforms: selectedPlatforms,
                        appLanguage: i18n.language || "en"
                    })
                });

                if (!response.ok) {
                    let errorText = `API Error: ${response.status}`;
                    try {
                      const errorData = await response.json();
                      errorText = errorData.detail || errorData.message || errorText;
                    } catch (e) { /* Ignore parsing error */ }
                    throw new Error(errorText);
                }

                const data: PlatformAnalysis[] = await response.json();
                setApiData(data);

                if (data && data.length > 0) {
                    const currentPlatformStillExists = data.some(p => p.metadata.platform === selectedTab);
                    let platformToFocus: SocialPlatform;
                    let analysisForFocusPlatform: PlatformAnalysis | undefined;

                    if (currentPlatformStillExists && selectedTab) {
                        platformToFocus = selectedTab;
                        analysisForFocusPlatform = data.find(p => p.metadata.platform === selectedTab);
                    } else {
                        platformToFocus = data[0].metadata.platform;
                        analysisForFocusPlatform = data[0];
                        setSelectedTab(platformToFocus);
                    }

                    if (analysisForFocusPlatform) {
                        const daysForFocusedPlatform = Object.keys(analysisForFocusPlatform.dailyHourlyEngagement);
                        const today = new Date();
                        const currentDayIndex = getDay(today); // date-fns: 0 for Sunday, 1 for Monday, etc.
                        const currentDayApiName = getApiDayKeyFromDateFns(currentDayIndex).toLowerCase(); 
                        
                        if (daysForFocusedPlatform.includes(currentDayApiName)) {
                            setSelectedDay(currentDayApiName);
                        } else if (selectedDay && daysForFocusedPlatform.includes(selectedDay)){
                            // Keep current selected day if it's still valid for this platform
                        } else {
                            setSelectedDay(daysForFocusedPlatform.length > 0 ? daysForFocusedPlatform[0] : null);
                        }
                    } else {
                        setSelectedDay(null); // Should not happen if data[0] exists
                    }
                } else {
                   setSelectedTab(null);
                   setSelectedDay(null);
                }
            } catch (err) {
                console.error("Error fetching AI best time:", err);
                setError(err instanceof Error ?
                           (err.message.includes("authenticated") ? t('errors.authRequired', "Authentication required") : err.message)
                           : t('errors.unknownError', "An unknown error occurred."));
                setApiData(null); // Clear data on error
                setSelectedTab(null);
                setSelectedDay(null);
            } finally {
                setIsLoading(false);
            }
        };

        // Handling for missing brandId (immediate, no debounce)
        if (!brandId) {
            setApiData(null);
            setError(null);
            setIsLoading(false);
            setSelectedTab(null);
            setSelectedDay(null);
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
                fetchTimeoutRef.current = null;
            }
            return;
        }

        // Handling for missing selectedPlatforms (immediate, no debounce)
        // Let renderContent handle the specific prompt for this case.
        if (selectedPlatforms.length === 0) {
            setApiData(null);
            setError(null); // Clear any previous API errors
            setIsLoading(false);
            setSelectedTab(null);
            setSelectedDay(null);
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
                fetchTimeoutRef.current = null;
            }
            return;
        }

        // If brandId and platforms are present, proceed with debounced fetch
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        // Clear previous data and error when new parameters are set for a fetch,
        // so old data isn't shown while waiting for debounce.
        setApiData(null);
        setError(null);
        // setIsLoading will be set to true inside internalFetchData when the fetch actually starts.

        fetchTimeoutRef.current = setTimeout(() => {
            internalFetchData();
        }, 2000); // 2-second debounce

        // Cleanup function for the useEffect
        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, [brandId, selectedPlatforms, i18n.language]); // Added i18n.language

    // Memoize chart data and recommendation calculation
    const currentPlatformAnalysis = useMemo(() => {
        return apiData?.find(p => p.metadata.platform === selectedTab);
    }, [apiData, selectedTab]);

    // Get available days for the current platform analysis
    const availableDays = useMemo(() => {
        return currentPlatformAnalysis ? Object.keys(currentPlatformAnalysis.dailyHourlyEngagement) : [];
    }, [currentPlatformAnalysis]);

    // Effect to reset selected day if it becomes invalid (e.g., platform change)
    useEffect(() => {
        if (currentPlatformAnalysis && selectedDay && !availableDays.includes(selectedDay)) {
            // If the selected day is no longer valid for the current platform,
            // default to the first available day for this platform.
            setSelectedDay(availableDays.length > 0 ? availableDays[0] : null);
        } else if (currentPlatformAnalysis && !selectedDay && availableDays.length > 0) {
            // If no day is selected but days are available, select the first one.
             setSelectedDay(availableDays[0]);
        }
    }, [currentPlatformAnalysis, availableDays, selectedDay]);

    const chartData = useMemo(() => {
        if (!currentPlatformAnalysis || !selectedDay || !currentPlatformAnalysis.dailyHourlyEngagement[selectedDay]) {
            return []; // Return empty array if data for the selected day isn't available
        }
        // Get data for the selected day
        const dailyData = currentPlatformAnalysis.dailyHourlyEngagement[selectedDay];
        return dailyData.map(item => ({
            name: `${item.hour}h`,
            hour: item.hour,
            score: item.score,
            reason: item.reason
        })) || [];
    }, [currentPlatformAnalysis, selectedDay]); // Depends on selectedDay now

    // --- Render Logic ---
    const renderContent = () => {
        if (!brandId || selectedPlatforms.length === 0) {
            return (
                <div className="text-center text-gray-500 py-10 px-4">
                    <Lightbulb className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    {t('aiBestTime.selectPrompt', 'Select a Brand and at least one Platform to see AI recommendations.')}
                </div>
            );
        }

        if (isLoading) {
            return (
                // Skeleton Loader
                <div className="space-y-4 animate-pulse">
                    {/* Placeholder for chart area */} 
                    <div className="h-[250px] w-full bg-gray-200 rounded-lg"></div>
                    {/* Placeholder for suggestion text */}
                    <div className="flex items-center gap-2">
                        <div className="h-4 w-4 bg-gray-200 rounded-full"></div>
                        <div className="h-3 w-3/4 bg-gray-200 rounded"></div>
                    </div>
                    {/* Placeholder for best/worst days */}
                    <div className="grid grid-cols-2 gap-4 pt-4">
                        <div>
                             <div className="h-3 w-1/4 bg-gray-200 rounded mb-2"></div>
                            <div className="flex flex-wrap gap-1.5">
                                <div className="h-4 w-12 bg-gray-200 rounded-full"></div>
                                <div className="h-4 w-16 bg-gray-200 rounded-full"></div>
                            </div>
                        </div>
                         <div>
                             <div className="h-3 w-1/4 bg-gray-200 rounded mb-2"></div>
                            <div className="flex flex-wrap gap-1.5">
                                <div className="h-4 w-14 bg-gray-200 rounded-full"></div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center text-red-600 py-10 px-4">
                    <AlertTriangle className="mx-auto h-8 w-8 text-red-500 mb-2" />
                    {t('aiBestTime.errorLoading', 'Error loading recommendations:')} {error} 
                </div>
            );
        }

        // Check if there's data for the selected day specifically
        if (!apiData || apiData.length === 0 || !currentPlatformAnalysis || !selectedDay || chartData.length === 0) {
            return (
                <div className="text-center text-gray-500 py-10 px-4">
                    <Lightbulb className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                     {t('aiBestTime.noDataForDay', 'Please choose a brand AND a platform to start')}
                </div>
            );
        }

        // If we have data, render the chart
        return (
            <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                            dataKey="hour"
                            tickFormatter={formatHourTick}
                            tickLine={false}
                            axisLine={false}
                            interval={2} // Show label every 3 hours (0, 3, 6...)
                            fontSize={12}
                            padding={{ left: 10, right: 10 }}
                        />
                        <YAxis 
                            domain={[0, 10]} 
                            ticks={[0, 2.5, 5, 7.5, 10]}
                            axisLine={false}
                            tickLine={false}
                            fontSize={10} 
                            width={30} // Allocate space for Y-axis labels
                        />
                         <Tooltip
                            cursor={{ fill: 'hsl(var(--muted))', fillOpacity: 0.3 }}
                            content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                        <div className="rounded-lg border bg-white p-2 shadow-sm text-xs">
                                            <div className="grid grid-cols-1 gap-1">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-muted-foreground">{t('aiBestTime.tooltipHour', 'Hour:')}</span>
                                                    <span className="font-bold">{label}</span>
                                                </div>
                                                 <div className="flex items-center justify-between">
                                                    <span className="text-muted-foreground">{t('aiBestTime.tooltipScore', 'Score:')}</span>
                                                    <span className="font-bold">{data.score?.toFixed(1)}</span>
                                                </div>
                                                 {data.reason && (
                                                      <div className="flex flex-col text-left mt-1">
                                                        <span className="text-muted-foreground text-[10px]">{t('aiBestTime.tooltipReason', 'Reason:')}</span>
                                                        <span className="font-normal text-[11px] whitespace-normal">{data.reason}</span>
                                                    </div>
                                                  )}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        {/* Single Bar (Primary Purple) */}
                        <Bar dataKey="score" fill="var(--color-score)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-xl border border-primary-600 shadow-lg">
            {/* Card Header */}
            <div className="p-4 sm:p-6 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-1">
                    <Lightbulb className="w-5 h-5 text-primary-600" />
                    <h2 className="text-lg font-semibold text-gray-900">
                        {brandName ? (
                            <>
                                {t('aiBestTime.titleForBrandPrefix', "Meilleur moment selon l'IA pour")}{' '}
                                <span className="text-primary-700">{brandName}</span>
                            </>
                        ) : (
                            t('aiBestTime.title', 'AI Best Time to Post')
                        )}
                    </h2>
                </div>
                <p className="text-sm text-gray-500">{t('aiBestTime.description', 'Hourly engagement score based on AI analysis')}</p>
            </div>

            {/* Platform Tabs (only if multiple platforms selected AND data exists) */}
            {apiData && apiData.length > 1 && (
                <div className="px-4 sm:px-6 pt-4 border-b border-gray-200">
                    <div className="flex space-x-2 overflow-x-auto">
                        {apiData.map((platformData) => (
                            <button
                                key={platformData.metadata.platform}
                                onClick={() => setSelectedTab(platformData.metadata.platform)}
                                className={`p-2 rounded-md border-b-2 flex items-center justify-center ${
                                    selectedTab === platformData.metadata.platform
                                        ? 'border-primary-600 bg-primary-50'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                                } transition-colors duration-150 ease-in-out`}
                                aria-label={t(`platforms.${platformData.metadata.platform}`, capitalize(platformData.metadata.platform))}
                                title={t(`platforms.${platformData.metadata.platform}`, capitalize(platformData.metadata.platform))}
                            >
                                <SocialIcon platform={platformData.metadata.platform} size={20} />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Day Tabs (only if data exists and there are days) */}
            {currentPlatformAnalysis && availableDays.length > 0 && (
                 <div className="px-4 sm:px-6 pt-3 border-b border-gray-200">
                    <div className="flex space-x-3 overflow-x-auto">
                        {availableDays.map((day) => (
                            <button
                                key={day}
                                onClick={() => setSelectedDay(day)}
                                className={`pb-1.5 px-1 text-xs font-medium border-b-2 ${
                                    selectedDay === day
                                        ? 'border-primary-600 text-primary-700'
                                        : 'border-transparent text-gray-500 hover:text-gray-600 hover:border-gray-300'
                                }`}
                            >
                                {/* Use translation key format like 'days.lundi' */}
                                {t(`days.${day.toLowerCase()}`, capitalize(day))}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Card Content (Chart or Placeholder/Loading/Error) */}
            <div className="px-4 py-2 sm:px-6 sm:py-3">
                {renderContent()}
            </div>

            {/* Footer with Recommendations */}
            {currentPlatformAnalysis && selectedDay && !isLoading && !error && ( // Ensure selectedDay is also set
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                    {/* AI Suggestion - Added padding here */}
                    {currentPlatformAnalysis.nextBestTime && (
                         <div className="flex items-start gap-2 text-gray-700 px-4 sm:px-6 py-3 text-sm">
                            <Lightbulb className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                            <div>
                                {/* Assuming nextBestTime format is like "mercredi 11:00" */}
                                <span className="font-semibold">{t('aiBestTime.suggestionPrefix', 'AI Suggestion:')}</span> {t('aiBestTime.suggestionBody', 'Next best time to post is around')} <span className="font-bold text-primary-700">{capitalize(currentPlatformAnalysis.nextBestTime)}</span>.
                            </div>
                        </div>
                    )}

                    {/* Top Recommendations Display (Optional: Could be filtered by selectedDay or shown all) */}
                    {/* For now, let's show all top recommendations, maybe add day info */}
                    {currentPlatformAnalysis.topRecommendations && currentPlatformAnalysis.topRecommendations.length > 0 && (
                        <div className="px-4 sm:px-6 pt-4 pb-2 border-t border-gray-200">
                            <h4 className="text-xs font-semibold text-gray-600 mb-2">{t('aiBestTime.topRecommendationsTitle', 'Top Recommended Times:')}</h4>
                            <ul className="space-y-2">
                                {currentPlatformAnalysis.topRecommendations.slice(0, 3).map((rec, index) => ( // Show top 3
                                    <li key={index} className="text-xs text-gray-600">
                                        <span className="font-semibold">{t(`days.${rec.day.toLowerCase()}`, capitalize(rec.day))}:</span> {rec.startTime} - {rec.endTime}
                                        {rec.reason && <span className="italic text-gray-500 ml-1">- {rec.reason}</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Best/Worst Days */}
                    {currentPlatformAnalysis.bestDays && currentPlatformAnalysis.worstDays && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4 sm:px-6 pt-4 pb-4 border-t border-gray-200">
                            <div>
                                <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-2">
                                    <CheckCircle className="w-3.5 h-3.5 text-green-500"/>
                                    {t('aiBestTime.bestDays', 'Best Days')}
                                </h4>
                                <div className="flex flex-wrap gap-1.5">
                                    {currentPlatformAnalysis.bestDays.map(day => (
                                        <span key={day} className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">{t(`days.${day.toLowerCase()}`, capitalize(day))}</span>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-2">
                                    <XCircle className="w-3.5 h-3.5 text-red-500"/>
                                    {t('aiBestTime.avoidDays', 'Days to Avoid')}
                                </h4>
                                <div className="flex flex-wrap gap-1.5">
                                    {currentPlatformAnalysis.worstDays.map(day => (
                                        <span key={day} className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-medium">{t(`days.${day.toLowerCase()}`, capitalize(day))}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {currentPlatformAnalysis.platformSpecificNotes && (
                        <div className="px-4 sm:px-6 pt-3 pb-4 border-t border-gray-200">
                            <p className="text-xs text-gray-500 italic">{currentPlatformAnalysis.platformSpecificNotes}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Update style tag */}
            <style>{`:root { --color-score: ${chartConfig.score.color}; }`}</style>
        </div>
    );
}; 